import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { ArrowLeft, ArrowRight, Loader2, Clock, Flame } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuizPage() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuestions();
    trackEvent("quiz_started");
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/quiz/questions`);
      setQuestions(response.data.questions);
    } catch (error) {
      toast.error("Failed to load quiz questions");
    } finally {
      setLoading(false);
    }
  };

  const trackEvent = async (eventType, eventData = {}) => {
    try {
      await axios.post(`${API}/analytics/track`, { event_type: eventType, event_data: eventData });
    } catch (error) {
      console.error("Failed to track event:", error);
    }
  };

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        question_id: questionId,
        answer: option.label,
        points: option.points
      }
    }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const answersList = Object.values(answers);
      const response = await axios.post(
        `${API}/quiz/submit`,
        { answers: answersList },
        { headers: getAuthHeaders() }
      );
      
      navigate(`/quiz/results/${response.data.quiz_id}`);
    } catch (error) {
      toast.error("Failed to submit quiz");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const isAnswered = answers[question?.id];
  const allAnswered = questions.every(q => answers[q.id]);

  return (
    <div className="py-8 md:py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Clock className="w-4 h-4" />
            <span>5 minutes</span>
            <span className="mx-2">•</span>
            <span className="text-emerald-600 font-medium">Free, no sign-up required</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Quick Test</h1>
          <p className="text-muted-foreground">
            Answer honestly based on facts, not hopes — this measures where you actually are.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                {currentQuestion + 1}
              </span>
              <span className="text-muted-foreground">of {questions.length}</span>
            </span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" data-testid="quiz-progress" />
        </div>

        {/* Question Card */}
        <Card className="mb-6 border-2" data-testid="quiz-question-card">
          <CardContent className="p-6 md:p-8">
            {/* Question Area Tag */}
            {question?.area && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                <Flame className="w-3 h-3" />
                {question.area}
              </div>
            )}
            
            <h2 className="text-lg md:text-xl font-semibold mb-6">{question?.question}</h2>
            
            <RadioGroup
              value={answers[question?.id]?.answer || ""}
              onValueChange={(value) => {
                const option = question.options.find(o => o.label === value);
                if (option) handleAnswer(question.id, option);
              }}
              className="space-y-3"
            >
              {question?.options.map((option, index) => (
                <div key={option.label} className="relative">
                  <RadioGroupItem
                    value={option.label}
                    id={`option-${option.label}`}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={`option-${option.label}`}
                    className="quiz-option peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                    data-testid={`quiz-option-${option.label}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                        answers[question?.id]?.answer === option.label
                          ? 'bg-primary text-white'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {option.label}
                      </span>
                      <span className="text-sm leading-relaxed pt-0.5">{option.text}</span>
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="rounded-full"
            data-testid="quiz-prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentQuestion === questions.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="rounded-full px-6 md:px-8"
              data-testid="quiz-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  See My Results
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!isAnswered}
              className="rounded-full"
              data-testid="quiz-next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Question indicators */}
        <div className="flex justify-center gap-1.5 mt-8 flex-wrap">
          {questions.map((q, index) => (
            <button
              key={q.id}
              onClick={() => setCurrentQuestion(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                index === currentQuestion
                  ? "bg-primary scale-125"
                  : answers[q.id]
                  ? "bg-primary/50"
                  : "bg-muted hover:bg-muted-foreground/30"
              }`}
              data-testid={`quiz-indicator-${index}`}
              aria-label={`Go to question ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
